// Pure helpers for the practice-preferences form's venue/day feasibility
// display. Split out so the day-label lookup and the "what's still reachable"
// rule are each testable without mounting the form.

import type {
  FeasibleVenue, PracticePairing, PracticeTimeRangeFault, PracticeTimeRangeInput, PracticeWeekday,
} from '@region13/shared-types'
import {
  formatTime12,
  practiceTimeRangeFault,
  PRACTICE_EARLIEST_START,
  PRACTICE_EARLIEST_END,
  MIN_PRACTICE_MINUTES,
} from '@region13/shared-types'

// Short form ("Mon") shared by weekdayList's venue annotation and the day
// chips (DayPreferencePicker); full form ("Monday") backs dayStateLabel's
// spoken state. Two different purposes, so both stay - but each has exactly
// one definition, here, not a second copy wherever it's consumed.
export const DAY_LABEL: Record<number, string> = { 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri' }
const DAY_FULL: Record<number, string> = {
  1: 'Monday', 2: 'Tuesday', 3: 'Wednesday', 4: 'Thursday', 5: 'Friday',
}

/** "Mon, Wed, Fri" for a venue's permit weekdays. */
export function weekdayList(weekdays: number[]): string {
  return weekdays.map((d) => DAY_LABEL[d] ?? String(d)).join(', ')
}

/** Weekdays reachable given what the coach has ranked. Before anything is
 *  ranked, every feasible venue is still in play, so the union is over all of
 *  them: graying out a day the coach could still reach by ranking a different
 *  venue would be wrong. */
export function reachableWeekdays(venues: FeasibleVenue[], ranked: string[]): Set<number> {
  const inPlay = ranked.length === 0 ? venues : venues.filter((v) => ranked.includes(v.slug))
  const out = new Set<number>()
  for (const v of inPlay) for (const d of v.weekdays) out.add(d)
  return out
}

/** A weekday's two answers at once: where it sits in the ranked order, and
 *  whether the coach says it cannot work at all. The two lists are disjoint,
 *  which `cycleDay` maintains, so a day is in exactly one of three states. */
export interface DayState {
  ranked: PracticeWeekday[]
  blocked: PracticeWeekday[]
}

/** One click on a day chip: unset -> ranked (appended last) -> blocked ->
 *  unset. Spec D5 chose one three-state control over a ranked row plus a
 *  separate blackout row precisely so "ranked AND blocked" is not
 *  representable, and this function is where that guarantee lives. */
export function cycleDay(state: DayState, day: PracticeWeekday): DayState {
  if (state.ranked.includes(day)) {
    // Ranks are positional, so dropping this day promotes everything after it.
    return { ranked: state.ranked.filter((d) => d !== day), blocked: [...state.blocked, day] }
  }
  if (state.blocked.includes(day)) {
    return { ranked: state.ranked, blocked: state.blocked.filter((d) => d !== day) }
  }
  return { ranked: [...state.ranked, day], blocked: state.blocked }
}

/** The chip's accessible name. A three-state control is only usable if the
 *  state is spoken, so this is a requirement and not decoration. */
export function dayStateLabel(day: PracticeWeekday, state: DayState): string {
  const name = DAY_FULL[day] ?? String(day)
  const rank = state.ranked.indexOf(day)
  if (rank !== -1) return `${name}, ranked ${rank + 1}`
  if (state.blocked.includes(day)) return `${name}, does not work`
  return `${name}, no preference`
}

/** Shown when a coach has blocked every day a permit can actually cover. This
 *  SAVES: it is a legitimate answer, and the consequence is an unassigned team
 *  the operator places by hand (spec D3). Refusing it would force the coach to
 *  lie about their availability, which is worse than a hand placement.
 *
 *  Measured against `reachable`, not against all five weekdays: if only Monday
 *  and Tuesday carry a permit, blocking both leaves nothing, and saying
 *  otherwise because Wednesday is unblocked would be false comfort. */
export function allDaysBlockedWarning(state: DayState, reachable: Set<number>): string | null {
  if (state.blocked.length === 0 || reachable.size === 0) return null
  const open = [...reachable].filter((d) => !state.blocked.includes(d as PracticeWeekday))
  if (open.length > 0) return null
  return (
    'You have marked every day we have a permit for as one that does not work. ' +
    'Your answer is saved, and the scheduler will place this team by hand.'
  )
}

/** Shown beside every unlit venue (spec D7). Names Butler as likely rather
 *  than promised, because there is no permit there yet.
 *
 *  It used to commit to the weekday ("your day of the week will not change"),
 *  and the re-home solver does not keep that promise. `_rehome_score`
 *  (container/app/practice.py) ranks candidates by (same venue, lit,
 *  `score_slot`) with the team's current weekday carrying no weight, and
 *  `_rehome_one` deliberately RELEASES that weekday so another slot may use
 *  it. The coach's ranked days are scored again, through the same `score_slot`
 *  the first placement used, so a highly-ranked day often survives the move --
 *  but "often" is not "will not change", and the autumn lighted-field move is
 *  the single most predictable re-home of the season to be wrong about. */
export const UNLIT_AUTUMN_ALERT =
  'In mid-October or early November this practice moves to a lighted field, most likely Octavia Butler. ' +
  'We use your ranked days again to choose the new slot, so we will try to keep your day of the week, ' +
  'but the day and the time can both change.'

/** Venues a coach may actually rank. A back-to-back coach gets lit venues
 *  only (spec D6): unlit fields lose their practices in the autumn, so a pair
 *  placed on one cannot stay together. Triggered by the WISH, not the outcome,
 *  because at survey time nobody knows what they will be placed into. */
export function rankableVenues(venues: FeasibleVenue[], pairing: PracticePairing | null): FeasibleVenue[] {
  if (pairing !== 'back_to_back') return venues
  const lit = venues.filter((v) => v.lit)
  // Never hand back an empty list: a coach who cannot answer is worse than a
  // coach who is warned. Impossible at every age today, but permits change.
  return lit.length > 0 ? lit : venues
}

/** Titles of venues the coach had ranked that the lit filter just removed, so
 *  the form can say so instead of discarding an answer silently. */
export function droppedByLitFilter(
  venues: FeasibleVenue[], ranked: string[], pairing: PracticePairing | null,
): string[] {
  const allowed = new Set(rankableVenues(venues, pairing).map((v) => v.slug))
  return ranked
    .filter((slug) => !allowed.has(slug))
    .map((slug) => venues.find((v) => v.slug === slug)?.title ?? slug)
}

export function litFilterNotice(droppedTitles: string[]): string | null {
  if (droppedTitles.length === 0) return null
  const names = droppedTitles.join(', ')
  const verb = droppedTitles.length === 1 ? 'is' : 'are'
  return `${names} ${verb} no longer offered for this team, because back to back practices have to be on a lighted field. Your other choices are unchanged.`
}

/** 6U teams may share a subfield. Deliberately states only that, with no
 *  promise that the coach will be told who they are sharing with: nothing in
 *  the product does that today, and it would be a publish-side change with its
 *  own spec. */
export function sharedSubfieldNote(teamAge: number | null): string | null {
  return teamAge === 6 ? '6U teams sometimes share a field with another 6U team.' : null
}

/** Shown at the top of the Upper Division form (spec D4). UD is placed at the
 *  end of the day regardless of any requested time, which is why that form
 *  asks no time questions at all: collecting an answer we will overrule is
 *  what the separate variant exists to avoid. */
export const UPPER_DIVISION_INTRO =
  'Upper Division teams practice twice a week, and both practices are placed at the end of the day ' +
  'so the fields are free for younger teams first. Rank your days for each practice below.'

/** Shown instead of the form for a team that has no practice at all: 4U and
 *  5U (spec D10), or a team no longer being fielded this season (D11). */
export const EXCLUDED_TEAM_MESSAGE = 'This team does not have scheduled practice, so there is nothing to set here.'

/** Shown when the team's division has not been opened for preference
 *  collection yet. Deliberately different from EXCLUDED_TEAM_MESSAGE: that team
 *  will never have a form, this one will. It promises no notification, because
 *  nothing sends one when a division opens. */
export const NOT_OPEN_TEAM_MESSAGE =
  'Practice preferences are not open for this division yet. They will appear here once the division is finalized.'

/** The warning a coach sees before saving over an existing submission (spec
 *  D8). Mandatory, not decorative: spec D2 makes the latest revision set the
 *  place in line, so saving again does cost a coach ground, and a ranking
 *  system whose penalty is invisible is a trap.
 *
 *  It must not OVERSTATE the cost either, which is what it used to do ("slots
 *  are assigned in the order questionnaires were completed ... behind every
 *  coach who has submitted before you save again"). The solver
 *  (`container/app/practice_order.py`) reads submission time as
 *  `questionnaire_order`, the LAST rung of all three ladders, and `_bucket`
 *  places an Upper Division team ahead of everyone regardless of when it
 *  submitted. So a revision loses ground only against coaches who tie on every
 *  rung above it, never against the whole region. Told otherwise, a coach who
 *  finds a wrong blackout in their answer leaves it there, which is the one
 *  outcome this survey exists to prevent.
 *
 *  ⚠️ It names the ladder a coach can act on, NOT every rung. Plenty of others
 *  outrank submission time (`_RUNGS_BY_BUCKET` in `practice_order.py`: the
 *  board, special and no-child flags in every bucket, `youngest_in_group` and
 *  `three_or_more_teams` in bucket 2), and one omission is worth naming
 *  because it is a real coach-facing advantage: bucket 3's `coach_team_count`,
 *  which places a multi-team coach who asked for SEPARATE days ahead of a
 *  single-team coach of the same age. (A back-to-back coach is already
 *  bucket 2, which the sentence does name.)
 *
 *  Left out DELIBERATELY, for two reasons that survive scrutiny: the sentence
 *  does not claim to be exhaustive, and the rung is narrow — it separates only
 *  teams of the SAME age, after the board and special flags. A third reason
 *  was tried and is WRONG, recorded so it is not re-argued: "it can only ever
 *  be good news for the reader". It is good news for the multi-team coach; the
 *  single-team coach at that age would be reading that somebody else outranks
 *  them. That cuts toward omitting it, not toward including it.
 *
 *  This sentence has one job with a volunteer at 10pm: make a coach who
 *  spotted a wrong blackout feel able to fix it. A further clause about who
 *  else places first does not serve that job and does make the sentence
 *  likelier to be skimmed. Do not re-add it without a reason bigger than
 *  completeness.
 *
 *  `submittedAt` is `practice_preferences.submitted_at`, the queue key itself,
 *  so the date shown is exactly the value the solver will order by.
 *
 *  Still "earlier today", never "before today": among coaches who DO tie, the
 *  order is the moment of the latest revision, so a same-day submission sits
 *  ahead of a later one. "Before today" reads as a free same-day correction on
 *  exactly the day a coach is most likely to be revising. */
export function resubmissionWarning(
  submittedAt: string | null, revision: number, timeZone: string,
): string | null {
  if (!submittedAt || revision < 1) return null
  const when = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', timeZone })
    .format(new Date(submittedAt))
  return (
    `You submitted these preferences on ${when}. Saving again records a new revision, and the time of your ` +
    'latest revision is where you sit in line. It is the last thing the scheduler looks at: Upper Division ' +
    'teams are placed first, then coaches who asked for back to back practices, then younger teams before ' +
    'older ones. So revising moves you behind the coaches you would otherwise be tied with, including any of '
    + 'them who submitted earlier today, and no further back than that.'
  )
}

export interface UnsatisfiablePair { venue: string; weekday: number }

/** Ranked (venue, day) pairs no permit can honor. A day is only reported
 *  against a venue when NO ranked venue covers it: a coach who ranked two
 *  venues and can get Monday at one of them has not contradicted themselves,
 *  and warning anyway is noise.
 *
 *  Shares `reachableWeekdays`'s underlying data on purpose. The grayed-out day
 *  chips and this warning must never disagree, which is why both read the same
 *  feasible-venue list rather than each deriving its own.
 *
 *  ⚠️ `pairs.length` is NOT a count of problems. One uncovered day emits one
 *  pair per ranked venue, so ranking three venues and one impossible day gives
 *  three pairs describing a single contradiction. The only consumer,
 *  `unsatisfiableWarning`, reads `pairs[0]` and names one venue on purpose. */
export function unsatisfiablePairs(
  venues: FeasibleVenue[], rankedSlugs: string[], rankedDays: number[],
): UnsatisfiablePair[] {
  if (rankedSlugs.length === 0 || rankedDays.length === 0) return []
  const ranked = venues.filter((v) => rankedSlugs.includes(v.slug))
  const covered = new Set<number>()
  for (const v of ranked) for (const d of v.weekdays) covered.add(d)
  const out: UnsatisfiablePair[] = []
  for (const day of rankedDays) {
    if (covered.has(day)) continue
    for (const v of ranked) out.push({ venue: v.slug, weekday: day })
  }
  return out
}

/** Warn, never block (spec D7 of the v2 spec): the copy has to say the
 *  preferences are saved anyway, or a coach reads it as a rejection and
 *  changes a true answer to a false one. */
export function unsatisfiableWarning(pairs: UnsatisfiablePair[], venues: FeasibleVenue[]): string | null {
  const first = pairs[0]
  if (!first) return null
  const title = venues.find((v) => v.slug === first.venue)?.title ?? first.venue
  return (
    `We have no permit for ${title} on ${weekdayList([first.weekday])}. ` +
    'Your preferences are saved either way, and we will get as close as we can.'
  )
}

/** "18:00" -> "6:00 PM". The time inputs hold 24-hour values, but a phone
 *  renders them on a 12-hour clock, so a message quoting a time back at the
 *  coach has to speak the clock they were reading. The rule is the shared
 *  `formatTime12`, which hands an unreadable value back unchanged. */
export const clockLabel = formatTime12

/** A contradiction in the time answers: which one, and what the coach reads.
 *  The form needs both - the message to show, and the fault to know WHICH
 *  inputs to mark. */
export interface TimeRangeProblem {
  fault: PracticeTimeRangeFault
  message: string
}

/** The one message on this form that BLOCKS the save, and deliberately so:
 *  every other warning here describes a poor-but-true answer, while a
 *  contradiction is refused by the server anyway. Catching it in the form is
 *  what keeps the refusal attached to the fields that caused it, instead of a
 *  durable toast that outlives the card it came from.
 *
 *  WHICH fault a contradictory answer has is `practiceTimeRangeFault`'s to
 *  decide (shared with the coach worker's `validatePrefsInput`, so the two
 *  layers cannot name different faults for one answer); only the wording is
 *  this file's. */
export function timeRangeProblem(t: PracticeTimeRangeInput): TimeRangeProblem | null {
  const fault = practiceTimeRangeFault(t)
  if (!fault) return null
  if (fault === 'inverted') {
    return {
      fault,
      message:
        `Latest end (${clockLabel(t.timeLatest ?? '')}) is before earliest start ` +
        `(${clockLabel(t.timeEarliest ?? '')}). Check AM and PM on both.`,
    }
  }
  // ⚠️ EXHAUSTIVE ON PURPOSE. This used to end in a two-way ternary whose else
  // branch claimed `preferred_after_latest`, so a fault added to the union
  // landed there and printed confidently wrong copy about a field the coach may
  // not even have filled in. A switch with a `never` check makes the compiler
  // refuse the next addition instead.
  switch (fault) {
    case 'earliest_before_floor':
      return {
        fault,
        message:
          `Practices do not start before ${clockLabel(PRACTICE_EARLIEST_START)}. `
          + `Set your earliest start to ${clockLabel(PRACTICE_EARLIEST_START)} or later.`,
      }
    case 'latest_before_floor':
      return {
        fault,
        message:
          `Practices run at least ${MIN_PRACTICE_MINUTES} minutes, so the earliest one can end is `
          + `${clockLabel(PRACTICE_EARLIEST_END)}. Set your latest end to ${clockLabel(PRACTICE_EARLIEST_END)} or later.`,
      }
    case 'preferred_before_floor':
      return {
        fault,
        message:
          `Practices do not start before ${clockLabel(PRACTICE_EARLIEST_START)}. `
          + `Set your preferred start to ${clockLabel(PRACTICE_EARLIEST_START)} or later.`,
      }
    case 'preferred_before_earliest':
      return {
        fault,
        message: `Preferred start (${clockLabel(t.timePreferred ?? '')}) is before your earliest start `
          + `(${clockLabel(t.timeEarliest ?? '')}).`,
      }
    case 'preferred_after_latest':
      return {
        fault,
        message: `Preferred start (${clockLabel(t.timePreferred ?? '')}) is after your latest end `
          + `(${clockLabel(t.timeLatest ?? '')}).`,
      }
    default: {
      const exhaustive: never = fault
      return exhaustive
    }
  }
}
